/**
 * Verification Hub — shows verification steps with status badges.
 *
 * On the false → true transition of `overview.isFullyVerified` (the moment
 * KYC approves the final missing step), we fire CelebrationOverlay. The flag
 * is held in component state so the celebration plays exactly once per
 * session — re-entering the screen with already-verified status does NOT
 * replay it (we initialize `wasFullyVerified` to the current value on mount).
 */
import { useState, useEffect } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'expo-router'
// toast import removed — navigation replaces coming_soon toasts
import { IdCard, Car, ScanFace, ShieldCheck, FileBadge, Mail, Phone } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { VerificationStepCard } from '@/components/profile/VerificationStepCard'
import { CelebrationOverlay } from '@/components/ui/CelebrationOverlay'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { useVerificationOverview } from '@/lib/hooks/use-verification'
import { useAmlReadiness } from '@/lib/hooks/use-aml-compliance'
import { useSemanticColors, FONTS, SPACING, RADIUS } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { QUALITAS_AML_PARTNER } from '@/lib/constants/aml'
import { isCoveragePartnerBrandingVisible } from '@/lib/config/insurance-partners'
import { hasVerifiedAuthEmail, hasVerifiedAuthPhone } from '@/lib/utils/account-contact'
import { useAuth } from '@/lib/hooks/use-auth'
import type { VerificationStatus } from '@/lib/types/verification.types'

/** Height of one VerificationStepCard. The skeleton stands in for exactly this. */
const STEP_CARD_HEIGHT = 80

export default function VerificationScreen() {
  const { t } = useTranslation()
  // Same gate as compliance.tsx: this label rides on status copy, which is a
  // promise about a partner rather than a consent to one.
  const amlPartnerLabel = isCoveragePartnerBrandingVisible()
    ? QUALITAS_AML_PARTNER.displayName
    : t('aml.partner_generic')
  // La sesión de auth es la fuente de verdad del correo verificado; el store
  // solo lleva banderas de flujo.
  const { user: authUser, profile } = useAuth()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()
  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewFailed,
    refetch: refetchOverview,
  } = useVerificationOverview()
  const aml = useAmlReadiness()

  // A failed overview is not "nothing completed yet". Before this, a host
  // whose query errored read five grey "Pendiente" badges plus a locked AML
  // step — the screen told them they had done none of the work they had
  // actually finished, and gave them nothing to tap.
  //
  // Scoped to the OVERVIEW query alone, deliberately. Identity, licence, selfie
  // and the declaration all come from it; only the last card comes from
  // `useAmlDeclaration`. Folding `aml.isError` in here replaced five correct
  // rows with one error card whenever the AML query alone failed — trading one
  // wrong screen for another, and taking away the steps the user could still
  // act on. The AML card renders its own state.
  const stepsFailed = overviewFailed
  // `overview === undefined` is NOT loading when the query is disabled
  // (`enabled: !!userId`): TanStack reports isLoading false, isError false and
  // no data, which used to pin this screen on a skeleton with no retry and no
  // way out — the same forever-skeleton this pass exists to remove.
  // AML is a conditional capability. It cannot hold the KYC hub hostage while
  // a background query settles: someone checking their DUI, selfie or licence
  // must see those real states even if they have never needed Qualitas.
  const stepsLoading = !stepsFailed && overviewLoading

  const retrySteps = () => {
    void refetchOverview()
    void aml.refetch()
  }

  // Track the value at mount so we only celebrate the *transition*, not the
  // already-verified state on re-entry. Initial mount sets the baseline.
  const [wasFullyVerified, setWasFullyVerified] = useState(!!overview?.isFullyVerified)
  const [showCelebration, setShowCelebration] = useState(false)

  useEffect(() => {
    if (overview?.isFullyVerified && !wasFullyVerified) {
      setShowCelebration(true)
      setWasFullyVerified(true)
    }
  }, [overview?.isFullyVerified, wasFullyVerified])

  /*
   * El contacto que falta es un paso más de verificación — no un peaje.
   *
   * Y puede faltar de cualquiera de los dos lados, que es lo que hace simétrica
   * la regla: quien entró por teléfono arrastra un correo interno
   * (`@phone.tago.app`) que no es un buzón —nadie recibe un recibo ahí ni
   * recupera su acceso con él— y quien entró por correo todavía no tiene
   * teléfono. Antes cualquiera de los dos huecos detenía la entrada a la app y
   * se leía como «te devolvimos a registrarte». Ahora aparecen acá, con su
   * estado, y se atienden cuando la persona quiera.
   */
  const emailPending = !hasVerifiedAuthEmail(authUser, profile)
  const phonePending = !hasVerifiedAuthPhone(authUser)

  const statusLabel = (status: string) => t(`verification.status_${status}` as const)
  const licenseStatus = overview?.license.status ?? 'pending'
  const identityApproved = overview?.identity.status === 'approved'
  const selfieApproved = overview?.selfie.status === 'approved'
  const amlUnlocked = identityApproved && selfieApproved

  const amlStepStatus: VerificationStatus = (() => {
    if (!aml.status || aml.status === 'draft') return 'pending'
    if (aml.status === 'approved') return 'approved'
    if (aml.status === 'rejected' || aml.status === 'needs_update') return 'rejected'
    if (aml.status === 'under_review' || aml.status === 'submitted') return 'submitted'
    return 'pending'
  })()
  /*
   * SÍ SE MUESTRA CUANDO HACE FALTA — y antes no, y eso varó gente.
   *
   * La regla era `Boolean(aml.data)`: la tarjeta de Qualitas aparecía sólo si
   * YA existía una fila de declaración. Para el huésped verificado que nunca
   * la empezó, el desenlace era una puerta cerrada: la ficha de un vehículo con
   * cobertura de plataforma decía "Verificarme para reservar", llevaba al hub,
   * el hub mostraba identidad/licencia/selfie en verde —y NINGUNA tarjeta de
   * Qualitas— así que no había dónde responder el cuestionario. El servidor, en
   * cambio, sí lo exigía (`booking_eligibility` con `requires_aml`), así que la
   * reserva quedaba bloqueada sin salida visible. Dos usuarios lo reportaron el
   * mismo día con grabación.
   *
   * La declaración NO es un trámite universal: se exige al reservar un vehículo
   * con cobertura de plataforma y al publicar con Qualitas. Por eso la tarjeta
   * aparece cuando ya hay una fila (cualquier estado) O cuando la persona llegó
   * al punto donde puede necesitarla: identidad y selfie aprobadas. Antes de
   * eso sigue oculta, para no pedir el cuestionario a quien todavía no puede
   * ni reservar.
   */
  const showAmlStep = Boolean(aml.data) || amlUnlocked

  return (
    <ScreenContainer>
      {showCelebration ? (
        <CelebrationOverlay
          message={t('verification.celebration_title')}
          subMessage={t('verification.celebration_subtitle')}
          onComplete={() => setShowCelebration(false)}
        />
      ) : null}
      <View
        style={{
          paddingHorizontal: SPACING.lg,
          gap: SPACING.lg,
        }}
      >
        <ScreenHeader
          title={t('verification.title')}
          backTestID="verification.back"
          style={{ paddingHorizontal: 0, paddingTop: SPACING.sm, paddingBottom: 0 }}
        />

        {/* Subtitle */}
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
            lineHeight: 21,
          }}
        >
          {t('verification.subtitle')}
        </Text>

        {/* Fully Verified Banner */}
        {overview?.isFullyVerified && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: `${colors.success}14`,
              gap: SPACING.sm,
            }}
          >
            <ShieldCheck size={24} color={colors.success} strokeWidth={1.5} />
            {/* Banner copy, not the badge label. `status_approved` is the word
                that already sits on every approved step below, so reusing it
                here printed "Aprobado" six times on one screen and told the
                user nothing about what being fully verified unlocks. */}
            <View style={{ flex: 1, gap: SPACING.xs }}>
              <Text
                style={{
                  fontSize: 15,
                  fontFamily: FONTS.body,
                  color: colors.success,
                }}
              >
                {t('verification.celebration_title')}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  lineHeight: 18,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('verification.celebration_subtitle')}
              </Text>
            </View>
          </View>
        )}

        {/* Steps */}
        <View style={{ gap: SPACING.md }}>
          <Text
            style={{
              fontSize: 16,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('verification.steps_title')}
          </Text>

          {stepsLoading ? (
            // Same gap and same row height as the cards below, so the swap
            // from skeleton to steps moves nothing.
            <View style={{ gap: SPACING.md }} testID="verification.steps-skeleton">
              {[0, 1, 2, 3, 4].map((index) => (
                <Skeleton
                  key={index}
                  width="100%"
                  height={STEP_CARD_HEIGHT}
                  borderRadius={RADIUS.lg}
                  staggerIndex={index}
                />
              ))}
            </View>
          ) : stepsFailed ? (
            <View testID="verification.steps-error">
              <EmptyState
                variant="aurora"
                title={t('common.error_title')}
                description={t('common.error_description')}
                action={{ label: t('common.retry'), onPress: retrySteps }}
              />
            </View>
          ) : (
            <>
              {/*
                Los titulos salen del registro de lib/verification/steps.ts, no de
                claves propias de esta pantalla. Antes decia "Documento de
                identidad" mientras el checkout decia "Verifica tu DUI o
                pasaporte" y la ficha "Foto de tu DUI": tres nombres para el
                mismo tramite, que para quien los lee son tres tramites.

                Los subtitulos SI son de esta pantalla: aca hay espacio para
                explicar para que sirve cada paso, y en una lista compacta no.
              */}
              {emailPending && (
                <VerificationStepCard
                  icon={Mail}
                  title={t('steps.email.label')}
                  description={t('verification.email_subtitle')}
                  status="pending"
                  statusLabel={statusLabel('pending')}
                  onPress={() => router.push('/(auth)/verify-email')}
                  testID="verification.step-email"
                />
              )}

              {/* El teléfono no bloquea la entrada, pero sí la reserva: es el
                  contacto con el que un anfitrión ubica a quien lleva su carro.
                  Por eso su descripción dice para qué hace falta, en vez de
                  pedirlo a secas. */}
              {phonePending && (
                <VerificationStepCard
                  icon={Phone}
                  title={t('steps.phone.label')}
                  description={t('verification.phone_subtitle')}
                  status="pending"
                  statusLabel={statusLabel('pending')}
                  onPress={() => router.push('/(auth)/verify-phone')}
                  testID="verification.step-phone"
                />
              )}

              <VerificationStepCard
                icon={IdCard}
                title={t('steps.identity_document.label')}
                description={t('verification.identity_subtitle')}
                status={overview?.identity.status ?? 'pending'}
                statusLabel={statusLabel(overview?.identity.status ?? 'pending')}
                rejectionReason={overview?.identity.rejectionReason}
                inReviewLabel={t('verification.identity_in_review_notice')}
                onPress={() => router.push('/profile/verification/identity')}
                testID="verification.step-identity"
              />

              <VerificationStepCard
                icon={Car}
                title={t('steps.driver_license.label')}
                description={t('verification.license_subtitle')}
                status={licenseStatus}
                statusLabel={statusLabel(licenseStatus)}
                rejectionReason={overview?.license.rejectionReason}
                inReviewLabel={t('verification.license_in_review_notice')}
                onPress={() => router.push('/profile/verification/license')}
                testID="verification.step-license"
              />

              <VerificationStepCard
                icon={ScanFace}
                title={t('steps.selfie.label')}
                description={t('verification.selfie_subtitle')}
                status={overview?.selfie.status ?? 'pending'}
                statusLabel={statusLabel(overview?.selfie.status ?? 'pending')}
                rejectionReason={overview?.selfie.rejectionReason}
                inReviewLabel={t('verification.selfie_in_review_notice')}
                onPress={() => router.push('/profile/verification/selfie')}
                testID="verification.step-selfie"
              />

              {showAmlStep ? (
                <VerificationStepCard
                  icon={FileBadge}
                  title={t('aml.step_title', { partner: QUALITAS_AML_PARTNER.displayName })}
                  description={t('aml.step_subtitle')}
                  status={amlStepStatus}
                  statusLabel={statusLabel(amlStepStatus)}
                  rejectionReason={aml.data?.rejectionReason}
                  inReviewLabel={t('aml.status_pending_body', { partner: amlPartnerLabel })}
                  disabled={!amlUnlocked}
                  lockedReason={t('aml.step_locked')}
                  onPress={() => router.push('/profile/verification/compliance')}
                  testID="verification.step-compliance"
                />
              ) : null}
            </>
          )}
        </View>
      </View>
    </ScreenContainer>
  )
}
